import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSharedProfile } from '../context/SharedProfileContext';
import SharedProfileNavigation from './SharedProfileNavigation';
import ProfileSummaryHeader from './ProfileSummaryHeader';
import ConversionCTA from './ConversionCTA';
import GeniLoader from './GeniLoader';
import api from '../lib/api';

/**
 * SharedProfileWrapper
 * 
 * This component wraps real page components (Dashboard, OpenPositions, etc.)
 * when accessed via a shared profile URL (/shared/:shareId/...).
 * 
 * It:
 * 1. Extracts shareId from URL params
 * 2. Validates the share and fetches owner analytics
 * 3. Enters shared profile mode (sets API headers)
 * 4. Renders the child component with shared profile chrome (nav, header, CTA)
 * 5. Exits shared profile mode on unmount
 */
export default function SharedProfileWrapper({ children }) {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const { 
    enterSharedProfileMode, 
    exitSharedProfileMode, 
    isSharedProfileView,
    shareInfo 
  } = useSharedProfile();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ownerAnalytics, setOwnerAnalytics] = useState(null);

  useEffect(() => {
    const initSharedProfile = async () => {
      if (!shareId) {
        setError('No share ID provided');
        setLoading(false);
        return;
      }

      try {
        // Fetch share info and validate it exists
        // Using the existing public endpoint to get share info
        const response = await api.get(`/public/share/${shareId}/info`);
        const data = response.data;
        
        if (!data || !data.share_info) {
          throw new Error('Invalid share data');
        }

        // Enter shared profile mode with the shareId
        // This sets the X-Shared-Profile-ID header on all API requests
        enterSharedProfileMode(
          shareId,
          null, // data will be fetched by individual components
          data.owner_id,
          data.share_info
        );

        // Store owner analytics for the header
        setOwnerAnalytics(data.analytics || null);
        setLoading(false);
        
      } catch (err) {
        console.error('Error initializing shared profile:', err);
        
        if (err.response?.status === 404) {
          setError('This shared profile link is invalid or has expired.');
        } else if (err.response?.status === 401) {
          // User not authenticated - redirect to login with return URL
          navigate(`/login?redirect=/shared/${shareId}/dashboard`);
          return;
        } else {
          setError(err.response?.data?.detail || 'Failed to load shared profile');
        }
        setLoading(false);
      }
    };

    initSharedProfile();

    // Cleanup: exit shared profile mode when unmounting
    return () => {
      exitSharedProfileMode();
    };
  }, [shareId, enterSharedProfileMode, exitSharedProfileMode, navigate]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <GeniLoader size="md" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="text-6xl mb-6">🔗</div>
          <h2 className="text-2xl font-bold text-white mb-3">Link Not Found</h2>
          <p className="text-white/60 mb-6">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-white text-black font-bold rounded-sm hover:bg-gray-200 transition-colors"
          >
            Go to Your Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Render the wrapped component with shared profile chrome
  return (
    <>
      {/* Shared Profile Navigation (replaces normal navigation) */}
      <SharedProfileNavigation />
      
      {/* Main content area with padding for fixed nav */}
      <div className="pt-16">
        {/* Profile Summary Header */}
        {ownerAnalytics && (
          <div className="w-full max-w-[1600px] mx-auto px-4 md:px-8 pt-8">
            <ProfileSummaryHeader analytics={ownerAnalytics} />
          </div>
        )}
        
        {/* Render the actual page component */}
        {children}
        
        {/* Conversion CTA at bottom */}
        <div className="w-full max-w-[1600px] mx-auto px-4 md:px-8 pb-8">
          <ConversionCTA />
        </div>
      </div>
    </>
  );
}
